// The spike's minimal device-aware prompt (gate A2.1): one small DOM bar of key caps and action names that follows
// the device used last, re-rendered synchronously in the event that switched it (so it changes within the frame).
// The full HUD on Preact is phase A4's (the ui package); this stays small and DOM-only so it can move there.
// Every text comes from the strings table: the caps through the glyph table, the names through the action labels.
import type { DeviceFamily, Input } from "@rpg/runtime";

/** One prompt as shown: the action, the family its glyph came from, the cap's and the name's text. */
export interface PromptItem {
  action: string;
  family: DeviceFamily;
  key: string;
  cap: string;
  label: string;
}

export interface Prompt {
  readonly element: HTMLElement;
  /** Re-render now (the device, a binding or the actions changed). */
  render(): void;
  /** Which actions to prompt for, in order. */
  setActions(actions: readonly string[]): void;
  show(on: boolean): void;
  /** What is on screen. */
  state(): { shown: boolean; device: DeviceFamily | null; items: PromptItem[] };
}

const CSS = `
.gv-prompt{position:fixed;left:50%;bottom:calc(14px + env(safe-area-inset-bottom));transform:translateX(-50%);z-index:11;display:none;
  gap:14px;padding:6px 12px;border-radius:10px;background:rgba(20,28,24,.55);color:#f4efe2;font:500 13px/1.2 system-ui,sans-serif;
  pointer-events:none;white-space:nowrap}
.gv-prompt[data-shown]{display:flex}
.gv-prompt-item{display:flex;align-items:center;gap:6px}
.gv-prompt kbd{font:600 12px/1 system-ui,sans-serif;padding:3px 6px;border-radius:5px;background:#f4efe2;color:#1d2a1f;min-width:12px;text-align:center}
.gv-prompt[data-device=gamepad] kbd{border-radius:999px}
.gv-prompt[data-device=touch] kbd{background:transparent;color:#f4efe2;border:1px solid #f4efe2}
`;

/** Mount the prompt bar (hidden until `show(true)`). `glyphs` maps family → glyph key → string id; `label` gives an
 *  action's name. */
export function createPrompt(o: {
  input: Input;
  glyphs: Readonly<Partial<Record<DeviceFamily, Readonly<Record<string, string>>>>>;
  t: (id: string) => string;
  label: (action: string) => string;
  actions: readonly string[];
  parent?: HTMLElement;
}): Prompt {
  if (!document.getElementById("gv-prompt-css")) {
    const s = document.createElement("style");
    s.id = "gv-prompt-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
  const el = document.createElement("div");
  el.className = "gv-prompt";
  el.dataset.prompt = "";
  el.setAttribute("role", "status");
  (o.parent ?? document.body).appendChild(el);
  let actions = [...o.actions];
  let shown = false;
  let items: PromptItem[] = [];

  const render = (): void => {
    items = [];
    for (const action of actions) {
      const g = o.input.glyph(action, o.glyphs);
      if (g) items.push({ action, family: g.family, key: g.key, cap: o.t(g.glyph), label: o.label(action) });
    }
    el.dataset.device = o.input.controls.device ?? "keyboardMouse";
    el.replaceChildren(
      ...items.map((it) => {
        const span = document.createElement("span");
        span.className = "gv-prompt-item";
        span.dataset.action = it.action;
        span.dataset.family = it.family;
        const kbd = document.createElement("kbd");
        kbd.textContent = it.cap;
        const name = document.createElement("span");
        name.textContent = it.label;
        span.append(kbd, name);
        return span;
      }),
    );
  };
  o.input.controls.onDevice(render);
  o.input.rebinding.onChange(render);
  render();

  return {
    element: el,
    render,
    setActions(a) {
      actions = [...a];
      render();
    },
    show(on) {
      shown = on;
      if (on) el.dataset.shown = "";
      else el.removeAttribute("data-shown");
    },
    state: () => ({ shown, device: o.input.controls.device, items: items.map((i) => ({ ...i })) }),
  };
}
